import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  ShoppingCart, Plus, Building, Home, Check, ArrowLeft, Eye, X, Package, Tag, ChevronDown, ChevronRight, Search, Euro, AlertCircle
} from 'lucide-react';
import Carousel from '../../components/Carousel';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAlert } from '../../components/AlertProvider';
import { useAuth } from '../../contexts/AuthContext';
import { useGlobalErrorHandler } from '../../hooks/useGlobalErrorHandler';
import { ShieldAlert } from 'lucide-react';
import {
  calculatePercentagePrice,
  calculateUGPrice,
  detectTier,
  UGCalculationResult,
  PricingTier
} from '../../utils/pricingCalculations';
import {
  fetchGlobalTiersForClient,
  TierBreakdownResponse,
  PricingResult,
  buildTierGroupKey,
  extractUnitName
} from '../../utils/globalTierCalculations';

interface Consumable {
  id: number;
  title: string;
  description: string | null;
  unit: string;
  unit_id: number;
  volume_ml: number | null;
  marque?: string | null;
  gamme?: string | null;
  base_price: number;
  custom_price?: number;
  currency: string;
  batch_size: number | null;
  min_order_quantity: number | null;
  is_active?: boolean | null;
  tiers?: PricingTier[];
  discount_percentage?: number | null;
  free_units?: number | null;
}

interface SalesPoint {
  id: number;
  name: string;
  address: string | null;
  city: string | null;
  postal_code: string | null;
}

interface ClientAddress {
  id: number;
  sales_point_id: number | null;
  label: string | null;
  kind: 'billing' | 'shipping';
  address: string;
  city: string;
  postal_code: string;
  country: string;
  is_default: boolean;
}

interface CartItem {
  consumable: Consumable;
  quantity: number;
  unitPrice: number;
  batchSize: number;
  pricingCalculation?: UGCalculationResult;
  globalPricing?: PricingResult;
}

interface ProductQuantity {
  consumable: Consumable;
  quantity: number;
  unitPrice: number;
  batchSize: number;
  pricingCalculation?: UGCalculationResult;
  globalPricing?: PricingResult;
}

interface CategoryGroup {
  key: string;
  marque: string;
  gamme: string;
  unitName: string;
  products: ProductQuantity[];
  tiers: PricingTier[];
  totalLots: number;
}

interface GroupTotals {
  totalLots: number;
  totalUnits: number;
  batchSize: number;
  unitName?: string;
}

interface OrderRecapTotals {
  baseTotal: number;
  discountedTotal: number;
  creditNoteValue: number;
  finalHt: number;
  taxAmount: number;
  totalTtc: number;
  gainTotal: number;
}

const BuyerNewOrderTailwind: React.FC = () => {
  const TVA_RATE: number = 5.5;
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useAlert();
  const { handleError } = useGlobalErrorHandler();
  const [cart, setCart] = useState<CartItem[]>([]);
  const [consumables, setConsumables] = useState<Consumable[]>([]);
  const [salesPoints, setSalesPoints] = useState<SalesPoint[]>([]);
  const [addresses, setAddresses] = useState<ClientAddress[]>([]);
  const [clientProfile, setClientProfile] = useState<any>(null);
  const [showBlockedModal, setShowBlockedModal] = useState<boolean>(false);
  const [globalTiers, setGlobalTiers] = useState<TierBreakdownResponse | null>(null);

  const [selectedSalesPoint, setSelectedSalesPoint] = useState<number | null>(null);
  const [selectedShippingAddress, setSelectedShippingAddress] = useState<number | null>(null);
  const [notes, setNotes] = useState<string>('');
  const [clientReference, setClientReference] = useState<string>('');
  const [creditNoteAmount, setCreditNoteAmount] = useState<number>(0);
  const [creditNotePercentage, setCreditNotePercentage] = useState<number>(0);
  const [creditNoteType, setCreditNoteType] = useState<'amount' | 'percentage'>('amount');
  const [creditNoteReason, setCreditNoteReason] = useState<string>('');

  const [busy, setBusy] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [selectedProduct, setSelectedProduct] = useState<Consumable | null>(null);
  const [showProductModal, setShowProductModal] = useState<boolean>(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [productQuantities, setProductQuantities] = useState<Map<number, number>>(new Map());
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [productSearch, setProductSearch] = useState<string>('');
  const hasAutoExpandedRef = useRef<boolean>(false);

  useEffect(() => {
    void loadData();
  }, []);

  useEffect(() => {
    if (selectedSalesPoint) {
      const selectedSp = salesPoints.find(sp => sp.id === selectedSalesPoint);
      if (selectedSp) {
        setSelectedShippingAddress(-selectedSp.id);
      }
    }
  }, [selectedSalesPoint, salesPoints]);

  useEffect(() => {
    const fetchTiers = async () => {
      if (cart.length === 0 || !clientProfile?.id) {
        setGlobalTiers(null);
        return;
      }
      try {
        const cartItems = cart.map(item => ({
          consumable: {
            id: item.consumable.id,
            title: item.consumable.title,
            base_price: item.consumable.base_price,
            batch_size: item.consumable.batch_size,
            unit: item.consumable.unit,
            volume_ml: item.consumable.volume_ml ?? undefined,
            custom_price: item.consumable.custom_price,
            tiers: item.consumable.tiers,
            marque: item.consumable.marque,
            gamme: item.consumable.gamme
          },
          quantity: item.quantity
        }));
        const tiers = await fetchGlobalTiersForClient(api, clientProfile.id, cartItems);
        setGlobalTiers(tiers);
      } catch (error) {
        console.error('Error fetching global tiers:', error);
      }
    };
    void fetchTiers();
  }, [cart.map(i => `${i.consumable.id}:${i.quantity}`).join(','), clientProfile?.id]);

  const recapTotals = useMemo((): OrderRecapTotals => {
    const baseTotal = cart.reduce((sum, item) => sum + (item.quantity * item.unitPrice * item.batchSize), 0);
    const discountedTotal = cart.reduce((sum, item) => {
      if (item.pricingCalculation) return sum + item.pricingCalculation.total_cost;
      return sum + (item.quantity * item.unitPrice * item.batchSize);
    }, 0);
    const gainTotal = Math.max(0, baseTotal - discountedTotal);
    // Calculate credit note value based on type
    const creditNoteValue = creditNoteType === 'percentage'
      ? (discountedTotal * (creditNotePercentage / 100))
      : (creditNoteAmount || 0);
    const taxableAmount = Math.max(0, discountedTotal - creditNoteValue);
    const taxAmount = Math.round(taxableAmount * TVA_RATE) / 100;
    const totalTtc = taxableAmount + taxAmount;
    return { baseTotal, discountedTotal, creditNoteValue, finalHt: taxableAmount, taxAmount, totalTtc, gainTotal };
  }, [cart, TVA_RATE, creditNoteAmount, creditNotePercentage, creditNoteType]);

  const totalItems = useMemo((): number => {
    return cart.reduce((sum, item) => sum + item.quantity, 0);
  }, [cart]);

  const selectedSalesPointName = useMemo((): string | null => {
    if (!selectedSalesPoint) return null;
    const salesPoint = salesPoints.find(sp => sp.id === selectedSalesPoint);
    if (!salesPoint) return null;
    return `${salesPoint.name}${salesPoint.city ? ` · ${salesPoint.city}` : ''}`;
  }, [salesPoints, selectedSalesPoint]);

  useEffect(() => {
    if (!globalTiers || !globalTiers.has_global_tiers) return;
  }, [globalTiers]);

  useEffect(() => {
    if (salesPoints.length === 0 || selectedSalesPoint) return;
    setSelectedSalesPoint(salesPoints[0].id);
  }, [salesPoints, selectedSalesPoint]);

  useEffect(() => {
    if (!selectedSalesPoint) return;
    const salesPoint = salesPoints.find(sp => sp.id === selectedSalesPoint);
    if (!salesPoint?.address || !salesPoint.city) return;
    setSelectedShippingAddress(-salesPoint.id);
  }, [salesPoints, selectedSalesPoint]);

  useEffect(() => {
    if (addresses.length === 0 || selectedSalesPoint) return;
    const shippingAddresses = addresses.filter(a => a.kind === 'shipping');
    if (shippingAddresses.length > 0 && !selectedShippingAddress) {
      const defaultShipping = shippingAddresses.find(a => a.is_default) || shippingAddresses[0];
      setSelectedShippingAddress(defaultShipping.id);
    }
  }, [addresses, selectedSalesPoint, selectedShippingAddress]);

  const createSalesPointAddress = (salesPoint: SalesPoint): ClientAddress => ({
    id: -salesPoint.id,
    sales_point_id: salesPoint.id,
    label: `${salesPoint.name} (Point de vente)`,
    kind: 'shipping',
    address: salesPoint.address || '',
    city: salesPoint.city || '',
    postal_code: salesPoint.postal_code || '',
    country: 'France',
    is_default: false,
  });

  const filteredAddresses = (kind: 'billing' | 'shipping'): ClientAddress[] => {
    // For billing, use regular addresses
    if (kind === 'billing') {
      const regularAddresses = addresses.filter(a => a.kind === kind);
      if (regularAddresses.length === 0 && clientProfile) {
        if (clientProfile.address && clientProfile.city && clientProfile.postal_code) {
          return [{
            id: -999,
            sales_point_id: null,
            label: `${clientProfile.company_name} (Profil)`,
            kind,
            address: clientProfile.address,
            city: clientProfile.city,
            postal_code: clientProfile.postal_code,
            country: clientProfile.country || 'France',
            is_default: true,
          }];
        }
      }
      return regularAddresses;
    }

    // For shipping: use sales point address only
    if (selectedSalesPoint) {
      const selectedSp = salesPoints.find(sp => sp.id === selectedSalesPoint);
      if (selectedSp) {
        return [createSalesPointAddress(selectedSp)];
      }
    }

    return [];
  };

  const loadData = async (): Promise<void> => {
    setBusy(true);
    try {
      const [resPrices, resSalesPoints, resAddresses, resProfile] = await Promise.all([
        api.get('/clients/me/pricing'),
        api.get('/clients/me/sales-points'),
        api.get('/clients/me/addresses'),
        api.get('/clients/me'),
      ]);
      const products = Array.isArray(resPrices.data) ? resPrices.data : [];
      const mappedProducts = products.filter((p: any) => p.is_active !== false).map((p: any) => ({
        ...p,
        id: p.consumable_id,
        tiers: p.tiers || [],
      }));
      setConsumables(mappedProducts);
      const spList: SalesPoint[] = Array.isArray(resSalesPoints.data) ? resSalesPoints.data : [];
      setSalesPoints(spList);
      // Auto-select first sales point if available
      if (spList.length > 0 && !selectedSalesPoint) {
        setSelectedSalesPoint(spList[0].id);
      }
      setAddresses(Array.isArray(resAddresses.data) ? resAddresses.data : []);
      setClientProfile(resProfile.data);
      if (resProfile.data.status === 'bloque' || user?.status === 'bloque') {
        setShowBlockedModal(true);
      }
    } catch (e: any) {
      handleError(e, 'Load error');
    } finally {
      setBusy(false);
    }
  };

  const getPrice = (consumableId: number): number => {
    const product = consumables.find(c => c.id === consumableId);
    return product?.custom_price ?? product?.base_price ?? 0;
  };

  const buildGroupedTotalsByUnitId = useCallback((items: CartItem[]): Map<number, GroupTotals> => {
    const map: Map<number, GroupTotals> = new Map();
    items.forEach((item: CartItem) => {
      const unitId = item.consumable.unit_id;
      if (!unitId) return;
      const batchSize = item.consumable.batch_size ?? 1;
      const current = map.get(unitId);
      const nextLots = (current?.totalLots ?? 0) + item.quantity;
      const nextUnits = (current?.totalUnits ?? 0) + (item.quantity * batchSize);
      const unitName = extractUnitName(item.consumable.title, item.consumable.unit) || item.consumable.unit;
      map.set(unitId, {
        totalLots: nextLots,
        totalUnits: nextUnits,
        batchSize: current?.batchSize ?? batchSize,
        unitName: current?.unitName ?? unitName
      });
    });
    return map;
  }, []);

  const groupedTotalsByUnitId = useMemo((): Map<number, GroupTotals> => {
    return buildGroupedTotalsByUnitId(cart);
  }, [cart, buildGroupedTotalsByUnitId]);

  const calculateItemPricingWithGroupedLots = useCallback(
    (consumable: Consumable, itemLots: number, groupedLots: number): UGCalculationResult | undefined => {
      const tiers = consumable.tiers || [];
      if (tiers.length === 0) return undefined;

      const batchSize = consumable.batch_size ?? 1;
      const basePrice = getPrice(consumable.id);
      const orderedUnits = itemLots * batchSize;
      const tier = detectTier(groupedLots, tiers);

      if (!tier) {
        return {
          ordered_units: orderedUnits,
          free_units: 0,
          total_received: orderedUnits,
          unit_price: basePrice,
          total_cost: orderedUnits * basePrice,
          display_text: `${itemLots} lot${itemLots > 1 ? 's' : ''}`,
          tier_triggered: false
        };
      }

      let result: UGCalculationResult;
      if (tier.free_units !== null && tier.free_units !== undefined && tier.free_units > 0) {
        result = calculateUGPrice(orderedUnits, basePrice, tier.free_units, itemLots);
      } else if (tier.discount_percentage !== null && tier.discount_percentage !== undefined) {
        result = calculatePercentagePrice(orderedUnits, basePrice, tier.discount_percentage, itemLots);
      } else {
        result = {
          ordered_units: orderedUnits,
          free_units: 0,
          total_received: orderedUnits,
          unit_price: basePrice,
          total_cost: orderedUnits * basePrice,
          display_text: `${itemLots} lot${itemLots > 1 ? 's' : ''}`,
          tier_triggered: false
        };
      }

      result.tier_number = tier.tier_number;
      return result;
    },
    [getPrice]
  );

  const applyGroupedTierPricing = useCallback((items: CartItem[]): CartItem[] => {
    const groupedTotals = buildGroupedTotalsByUnitId(items);
    const result = items.map((item: CartItem) => {
      const groupedLots = groupedTotals.get(item.consumable.unit_id)?.totalLots ?? item.quantity;
      const pricingCalculation = calculateItemPricingWithGroupedLots(item.consumable, item.quantity, groupedLots);
      return { ...item, pricingCalculation };
    });
    return result;
  }, [buildGroupedTotalsByUnitId, calculateItemPricingWithGroupedLots]);

  const groupProductsByCategory = useCallback((): CategoryGroup[] => {
    const groups = new Map<string, CategoryGroup>();
    consumables.forEach((consumable) => {
      const unitName = extractUnitName(consumable.title, consumable.unit);
      const key = `${consumable.unit_id}|${buildTierGroupKey(consumable.marque, consumable.gamme, unitName)}`;
      if (!key) return;
      const quantity = productQuantities.get(consumable.id) || 0;
      const cartItem = cart.find(item => item.consumable.id === consumable.id);
      const batchSize = consumable.batch_size || 1;
      const unitPrice = getPrice(consumable.id);
      const groupTotals = groupedTotalsByUnitId.get(consumable.unit_id);
      const productData: ProductQuantity = {
        consumable,
        quantity,
        unitPrice,
        batchSize,
        pricingCalculation: cartItem?.pricingCalculation,
      };
      if (groups.has(key)) {
        const group = groups.get(key)!;
        group.products.push(productData);
        group.totalLots = groupTotals?.totalLots ?? group.totalLots;
      } else {
        groups.set(key, {
          key,
          marque: consumable.marque || '',
          gamme: consumable.gamme || '',
          unitName: unitName || consumable.unit,
          products: [productData],
          tiers: consumable.tiers || [],
          totalLots: groupTotals?.totalLots ?? 0,
        });
      }
    });
    return Array.from(groups.values()).sort((a, b) => a.key.localeCompare(b.key));
  }, [consumables, productQuantities, cart, globalTiers, getPrice, groupedTotalsByUnitId]);

  const categoryGroups = useMemo(() => groupProductsByCategory(), [groupProductsByCategory]);

  const filteredCategoryGroups = useMemo(() => {
    if (!productSearch.trim()) return categoryGroups;
    const search = productSearch.toLowerCase();
    return categoryGroups.map(group => ({
      ...group,
      products: group.products.filter(p =>
        p.consumable.title.toLowerCase().includes(search) ||
        p.consumable.marque?.toLowerCase().includes(search) ||
        p.consumable.gamme?.toLowerCase().includes(search)
      )
    })).filter(group => group.products.length > 0);
  }, [categoryGroups, productSearch]);

  useEffect(() => {
    if (consumables.length === 0 || hasAutoExpandedRef.current) {
      return;
    }
    hasAutoExpandedRef.current = true;
    setExpandedCategories(new Set(categoryGroups.map(g => g.key)));
  }, [consumables.length, categoryGroups]);

  const toggleCategory = (key: string): void => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const setProductQuantityDirect = (consumableId: number, newQty: number): void => {
    const product = consumables.find(c => c.id === consumableId);
    if (!product) return;
    const minOrderQty = product.min_order_quantity || 1;
    let validQty = newQty;
    if (newQty > 0 && newQty < minOrderQty) validQty = minOrderQty;
    setProductQuantities(prev => {
      const next = new Map(prev);
      if (validQty === 0) next.delete(consumableId);
      else next.set(consumableId, validQty);
      return next;
    });
  };

  useEffect(() => {
    const newCart: CartItem[] = [];
    productQuantities.forEach((quantity, consumableId) => {
      if (quantity > 0) {
        const consumable = consumables.find(c => c.id === consumableId);
        if (consumable) {
          newCart.push({
            consumable,
            quantity,
            unitPrice: getPrice(consumableId),
            batchSize: consumable.batch_size || 1,
          });
        }
      }
    });
    setCart(applyGroupedTierPricing(newCart));
  }, [productQuantities, consumables, applyGroupedTierPricing]);

  const validatePanachage = useCallback((): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];
    const groupedByUnitId = buildGroupedTotalsByUnitId(cart);
    for (const [, group] of groupedByUnitId.entries()) {
      const { totalUnits, batchSize, unitName } = group;
      if (batchSize <= 1) continue;
      if (totalUnits % batchSize !== 0) {
        const remaining = totalUnits % batchSize;
        const needed = batchSize - remaining;
        errors.push(
          `${unitName}: ${totalUnits} unités = ${(totalUnits / batchSize).toFixed(2)} lots. ` +
          `Ajoutez ${needed} unité${needed > 1 ? 's' : ''} ou retirez ${remaining} pour obtenir un multiple de ${batchSize}.`
        );
      }
    }
    return { valid: errors.length === 0, errors };
  }, [cart, buildGroupedTotalsByUnitId]);

  const validationErrors: string[] = useMemo(() => validatePanachage().errors, [validatePanachage]);

  const canSubmit = (): boolean => cart.length > 0 && selectedShippingAddress !== null && validationErrors.length === 0;

  const submit = async (): Promise<void> => {
    if (clientProfile?.status === 'bloque' || user?.status === 'bloque') {
      setShowBlockedModal(true);
      return;
    }
    if (validationErrors.length > 0) {
      showError('Quantités invalides. Veuillez corriger les erreurs de panachage.');
      return;
    }
    if (!canSubmit()) {
      showError('Veuillez sélectionner une adresse de livraison');
      return;
    }
    setSubmitting(true);
    try {
      let shippingAddr = addresses.find(a => a.id === selectedShippingAddress);
      if (!shippingAddr && selectedShippingAddress && selectedShippingAddress < 0) {
        shippingAddr = filteredAddresses('shipping').find(a => a.id === selectedShippingAddress);
      }
      const payload = {
        sales_point_id: selectedSalesPoint,
        shipping_address_id: selectedShippingAddress && selectedShippingAddress > 0 ? selectedShippingAddress : null,
        delivery_address: shippingAddr?.address,
        delivery_city: shippingAddr?.city,
        delivery_postal_code: shippingAddr?.postal_code,
        delivery_country: shippingAddr?.country,
        notes,
        client_reference: clientReference.trim() || null,
        tier_breakdown: globalTiers ? JSON.stringify(globalTiers.tier_breakdown) : null,
        credit_note_amount: creditNoteType === 'amount' ? (creditNoteAmount || 0) : 0,
        credit_note_percentage: creditNoteType === 'percentage' ? (creditNotePercentage || 0) : 0,
        credit_note_type: creditNoteType,
        credit_note_reason: (creditNoteType === 'amount' ? creditNoteAmount : creditNotePercentage) > 0 ? (creditNoteReason || '') : '',
        items: cart.map(item => ({ consumable_id: item.consumable.id, quantity: item.quantity })),
      };
      await api.post('/clients/me/orders', payload);
      showSuccess('Commande créée avec succès !');
      window.location.assign('/client/orders');
    } catch (e: any) {
      handleError(e, 'Submit error');
      showError(e.response?.data?.detail || 'Erreur lors de la création de la commande');
    } finally {
      setSubmitting(false);
      setShowConfirmModal(false);
    }
  };

  if (busy) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-950">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  const BlockedAccountModal = () => (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
      <div className="max-w-md w-full bg-white dark:bg-slate-900 rounded-2xl shadow-2xl p-8 text-center">
        <div className="w-20 h-20 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <ShieldAlert className="h-10 w-10 text-red-600 dark:text-red-400" />
        </div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-4">Compte Bloqué</h2>
        <p className="text-slate-600 dark:text-slate-400 mb-8">Votre compte est temporairement bloqué.</p>
        <button onClick={() => { setShowBlockedModal(false); navigate('/client/catalog'); }} className="w-full py-3 bg-slate-900 text-white rounded-xl font-bold">Retour</button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950 p-3 sm:p-6">
      {showBlockedModal && <BlockedAccountModal />}

      {/* Header */}
      <div className="max-w-7xl mx-auto mb-4 sm:mb-6">
        <button onClick={() => navigate('/client/catalog')} className="flex items-center gap-2 text-slate-600 hover:text-cyan-600 mb-3 sm:mb-4 text-sm sm:text-base">
          <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5" />Retour au catalogue
        </button>
        <h1 className="text-xl sm:text-3xl font-bold text-slate-900 dark:text-slate-100">Créer une commande</h1>
        <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400">Sélectionnez vos produits et validez votre commande</p>
      </div>

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden sticky top-6">
            {salesPoints.length > 0 && (
              <div className="p-4 border-b border-slate-100 dark:border-slate-800">
                <div className="flex items-center gap-2 mb-3">
                  <div className="p-1.5 rounded-lg bg-blue-100 dark:bg-blue-900/30"><Building className="h-4 w-4 text-blue-600" /></div>
                  <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">Point de vente</span>
                </div>
                <select value={selectedSalesPoint || ''} onChange={(e) => setSelectedSalesPoint(Number(e.target.value) || null)} className="w-full px-3 py-2.5 border border-slate-200 dark:border-slate-600 rounded-xl bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 transition-all">
                  {salesPoints.map(sp => (<option key={sp.id} value={sp.id}>{sp.name} {sp.city && `· ${sp.city}`}</option>))}
                </select>
              </div>
            )}

            <div className="p-4 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-1.5 rounded-lg bg-emerald-100 dark:bg-emerald-900/30"><Home className="h-4 w-4 text-emerald-600" /></div>
                <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">Livraison</span>
                {!selectedShippingAddress && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500 text-white font-medium ml-auto">Requis</span>}
              </div>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {filteredAddresses('shipping').map(addr => (
                  <label key={addr.id} className={`block p-3 rounded-xl border-2 cursor-pointer transition-all ${selectedShippingAddress === addr.id ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/20' : 'border-transparent bg-slate-50 dark:bg-slate-800'}`}>
                    <div className="flex items-start gap-2">
                      <input type="radio" checked={selectedShippingAddress === addr.id} onChange={() => setSelectedShippingAddress(addr.id)} className="mt-1 accent-emerald-500" />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{addr.label || 'Livraison'}</span>
                        {addr.is_default && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500 text-white font-medium ml-2">Défaut</span>}
                        <p className="text-xs text-slate-500 mt-1">{addr.address}, {addr.postal_code} {addr.city}</p>
                      </div>
                    </div>
                  </label>
                ))}
                {filteredAddresses('shipping').length === 0 && <p className="text-sm text-slate-500 p-2">Aucune adresse. </p>}
              </div>
            </div>

            <div className="p-4 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-1.5 rounded-lg bg-violet-100 dark:bg-violet-900/30"><Package className="h-4 w-4 text-violet-600" /></div>
                <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">Notes</span>
              </div>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Instructions spéciales..." rows={2} className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-800 resize-none" />
            </div>

            <div className="p-4 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-1.5 rounded-lg bg-blue-100 dark:bg-blue-900/30"><Tag className="h-4 w-4 text-blue-600" /></div>
                <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">Référence client</span>
                <span className="text-xs text-slate-400">(optionnel)</span>
              </div>
              <input type="text" value={clientReference} onChange={(e) => setClientReference(e.target.value)} placeholder="Votre référence..." className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-800" />
            </div>

            <div className="hidden lg:block p-4 bg-slate-50/50 dark:bg-slate-800/30">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-1.5 rounded-lg bg-cyan-100 dark:bg-cyan-900/30"><ShoppingCart className="h-4 w-4 text-cyan-600" /></div>
                <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">Récapitulatif</span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">Articles</span><span className="font-medium text-slate-900 dark:text-slate-100">{cart.length}</span></div>
                {recapTotals.baseTotal > recapTotals.discountedTotal && <div className="flex justify-between text-slate-400 line-through"><span>Prix de base HT</span><span>{recapTotals.baseTotal.toFixed(2)} €</span></div>}
                <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">Sous-total HT</span><span className="font-medium text-slate-900 dark:text-slate-100">{recapTotals.discountedTotal.toFixed(2)} €</span></div>
                {recapTotals.gainTotal > 0 && <div className="flex justify-between text-emerald-600"><span>Économie</span><span className="font-semibold">-{recapTotals.gainTotal.toFixed(2)} €</span></div>}
                {(creditNoteType === 'amount' ? creditNoteAmount : creditNotePercentage) > 0 && <div className="flex justify-between text-amber-600"><span>Remise supplémentaire {creditNoteType === 'percentage' ? `(${creditNotePercentage}%)` : ''}</span><span className="font-semibold">-{creditNoteType === 'percentage' ? (recapTotals.discountedTotal * (creditNotePercentage / 100)).toFixed(2) : creditNoteAmount.toFixed(2)} €</span></div>}
                <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">TVA {TVA_RATE}%</span><span className="font-medium text-slate-900 dark:text-slate-100">{recapTotals.taxAmount.toFixed(2)} €</span></div>
                <div className="pt-2 border-t border-slate-200 dark:border-slate-700 flex justify-between"><span className="font-bold text-slate-900 dark:text-slate-100">Total TTC</span><span className="font-bold text-cyan-600">{recapTotals.totalTtc.toFixed(2)} €</span></div>
              </div>
            </div>
          </div>
        </div>

        {/* Right column - Products */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-xl bg-gradient-to-br from-cyan-500 to-cyan-600 shadow-lg shadow-cyan-500/20"><Package className="h-5 w-5 text-white" /></div>
                  <div>
                    <h2 className="text-base font-bold text-slate-900 dark:text-slate-100">Catalogue</h2>
                    {/* <p className="text-xs text-slate-500">{filteredCategoryGroups.length} catégories</p> */}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs">
                  <button
                    type="button"
                    onClick={() => setExpandedCategories(new Set(filteredCategoryGroups.map(group => group.key)))}
                    className="px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                  >
                    Tout déplier
                  </button>
                  <button
                    type="button"
                    onClick={() => setExpandedCategories(new Set())}
                    className="px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                  >
                    Tout replier
                  </button>
                </div>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input type="text" value={productSearch} onChange={(e) => setProductSearch(e.target.value)} placeholder="Rechercher un produit..." className="w-full h-10 pl-10 pr-4 rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500" />
              </div>
            </div>

            <div className="max-h-[600px] overflow-y-auto">
              {filteredCategoryGroups.map((group) => {
                const isExpanded = expandedCategories.has(group.key);
                const sortedTiers = [...group.tiers].sort((a, b) => a.min_quantity - b.min_quantity);
                const activeTier = detectTier(group.totalLots, sortedTiers);
                const nextTier = sortedTiers.find((t) => t.min_quantity > group.totalLots);
                const remainingLots = nextTier ? Math.max(0, nextTier.min_quantity - group.totalLots) : null;
                return (
                  <div key={group.key} className={`border-b last:border-b-0 ${isExpanded ? 'border-slate-200 dark:border-slate-700' : 'border-slate-200 dark:border-slate-700'}`}>
                    <button type="button" onClick={() => toggleCategory(group.key)} className={`w-full px-4 py-3.5 flex items-center gap-3 transition-all ${isExpanded ? 'bg-gradient-to-r from-amber-50 via-orange-50 to-amber-100 text-slate-900 border-l-4 border-orange-400 shadow-sm ring-1 ring-orange-200/60 dark:from-slate-800 dark:via-slate-800 dark:to-slate-900 dark:text-white dark:ring-0' : 'bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-750 border-l-4 border-l-orange-300'}`}>
                      <div className={`p-1.5 rounded-lg transition-colors ${isExpanded ? 'bg-white/80 border border-orange-200 shadow-sm dark:bg-orange-500/20 dark:border-orange-400/40' : 'bg-orange-100 dark:bg-orange-900/30'}`}>
                        {isExpanded ? <ChevronDown className="h-4 w-4 text-orange-600 dark:text-orange-200" /> : <ChevronRight className="h-4 w-4 text-orange-600 dark:text-orange-400" />}
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <p className={`text-xs sm:text-sm font-bold truncate ${isExpanded ? 'text-slate-900 dark:text-white' : 'text-slate-800 dark:text-slate-100'}`}>
                          {group.marque} · {group.gamme}{group.unitName ? ` | ${group.unitName}` : ''}
                        </p>
                      </div>

                      <div className="flex items-center gap-1 sm:gap-1.5 flex-shrink-0">
                        {sortedTiers.map((tier) => {
                          const isActive = group.totalLots >= tier.min_quantity;
                          return (
                            <span
                              key={tier.tier_number}
                              title={`Palier ${tier.tier_number}: À partir de ${tier.min_quantity} lots`}
                              className={`text-[9px] sm:text-[10px] px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-full font-bold transition-all cursor-help ${isActive ? 'bg-emerald-500 text-white shadow-sm' : isExpanded ? 'bg-orange-200 text-orange-700 dark:bg-slate-600 dark:text-slate-300' : 'bg-slate-200 dark:bg-slate-600 text-slate-500 dark:text-slate-400'}`}
                            >
                              {tier.discount_percentage ? `-${tier.discount_percentage}%` : `+${tier.free_units}U`}
                            </span>
                          );
                        })}
                      </div>
                    </button>

                    {isExpanded && (remainingLots && remainingLots > 0 && nextTier) && (
                      <div className="mx-5 mb-2 p-2.5 rounded-lg bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-950/30 dark:to-red-950/30 border border-orange-200 dark:border-orange-900/50">
                        <div className="flex items-center justify-center text-xs">
                          <span className="text-orange-600 dark:text-orange-400 font-semibold">
                            +{remainingLots} lot{remainingLots > 1 ? 's' : ''} pour débloquer {nextTier.discount_percentage}% de remise
                          </span>
                        </div>
                      </div>
                    )}
                    {isExpanded && activeTier && !(remainingLots && remainingLots > 0 && nextTier) && (
                      <div className="mx-5 mb-2 p-2.5 rounded-lg bg-gradient-to-r from-emerald-50 to-emerald-100 dark:from-emerald-950/30 dark:to-emerald-900/30 border border-emerald-200 dark:border-emerald-900/50">
                        <div className="flex items-center justify-center text-xs">
                          <span className="inline-flex items-center gap-1.5 text-emerald-700 dark:text-emerald-400 font-semibold">
                            <Check className="h-3.5 w-3.5" />
                            Dernier palier débloqué
                          </span>
                        </div>
                      </div>
                    )}

                    {isExpanded && (
                      <div className="hidden sm:block px-5 py-2 bg-slate-100/50 dark:bg-slate-800/50 border-y border-slate-100 dark:border-slate-700/50 animate-in fade-in slide-in-from-top-2 duration-200">
                        <div className="flex items-center gap-4 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                          <div className="flex-1">Produit</div>
                          <div className="w-20 text-center">Palier</div>
                          <div className="w-20 text-right">Prix unit.</div>
                          <div className="w-20 text-right">Prix/lot</div>
                          <div className="w-16 text-center">Qté</div>
                          <div className="w-24 text-right">Total</div>
                        </div>
                      </div>
                    )}

                    {isExpanded && (
                      <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                        {group.products.map((item) => {
                          const quantity = item.quantity;
                          const basePrice = item.unitPrice;
                          const pricing = item.pricingCalculation;
                          const batchSize = item.consumable.batch_size ?? 1;
                          const minOrderQty = item.consumable.min_order_quantity || 1;
                          const unitPrice = pricing ? pricing.unit_price : basePrice;
                          const baseUnitPrice = basePrice;
                          const lotPrice = unitPrice * batchSize;
                          const lineTotal = pricing ? pricing.total_cost : quantity * basePrice * batchSize;
                          const baseLotPrice = baseUnitPrice * batchSize;
                          const hasDiscount = Boolean(pricing?.tier_triggered && lotPrice < baseLotPrice);
                          const groupedLotsForItem = groupedTotalsByUnitId.get(item.consumable.unit_id)?.totalLots ?? item.quantity;
                          const activeTierForItem = detectTier(groupedLotsForItem, sortedTiers);
                          const tierNumber = activeTierForItem?.tier_number ?? null;
                          const showTierBadge = quantity > 0 && Boolean(tierNumber);

                          return (
                            <div key={item.consumable.id} className={`px-3 sm:px-5 py-3 border-b border-slate-50 dark:border-slate-800/50 last:border-b-0 ${quantity > 0 ? 'bg-cyan-50/40 dark:bg-cyan-900/5' : 'hover:bg-slate-100/80 dark:hover:bg-slate-700/30'}`}>
                              {/* Desktop row */}
                              <div className="hidden sm:flex items-center gap-4">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{item.consumable.title}</p>
                                    <button onClick={() => { setSelectedProduct(item.consumable); setShowProductModal(true); }} className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded"><Eye className="h-3.5 w-3.5 text-slate-400" /></button>
                                  </div>
                                  <p className="text-[11px] text-slate-400">Lot de {batchSize} {item.consumable.unit}</p>
                                </div>
                                <div className="w-20 flex justify-center">
                                  {showTierBadge ? (
                                    <span className="px-2 py-1 rounded-md bg-emerald-500 text-white text-[10px] font-bold">P{tierNumber}</span>
                                  ) : (
                                    <span className="text-xs text-slate-400 dark:text-slate-500">—</span>
                                  )}
                                </div>
                                <div className="w-20 text-right">
                                  {hasDiscount && <p className="text-[10px] text-slate-400 line-through">{baseUnitPrice.toFixed(2)} €</p>}
                                  <p className={`text-xs font-medium ${hasDiscount ? 'text-emerald-600' : 'text-slate-500'}`}>{unitPrice.toFixed(2)} €</p>
                                </div>
                                <div className="w-20 text-right">
                                  {hasDiscount && <p className="text-[10px] text-slate-400 line-through">{baseLotPrice.toFixed(2)} €</p>}
                                  <p className={`text-sm font-semibold ${hasDiscount ? 'text-emerald-600' : 'text-slate-600'}`}>{lotPrice.toFixed(2)} €</p>
                                </div>
                                <div className="w-16">
                                  <input type="number" min={0} value={quantity === 0 ? '' : quantity} onChange={(e) => setProductQuantityDirect(item.consumable.id, e.target.value === '' ? 0 : (Number(e.target.value) || 0))} onBlur={(e) => { if (e.target.value === '') setProductQuantityDirect(item.consumable.id, 0); }} placeholder="0" className={`w-full h-8 rounded-lg border px-2 text-center text-sm font-semibold ${quantity > 0 ? 'border-cyan-300 bg-white text-cyan-600' : 'border-slate-200 bg-slate-50 text-slate-900'}`} />
                                </div>
                                <div className="w-24 text-right">
                                  {quantity > 0 ? (<p className="text-sm font-bold">{lineTotal.toFixed(2)} €</p>) : <p className="text-sm text-slate-300">—</p>}
                                </div>
                              </div>
                              {/* Mobile card */}
                              <div className="sm:hidden">
                                <div className="flex items-start justify-between gap-2 mb-2">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5">
                                      <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{item.consumable.title}</p>
                                      <button onClick={() => { setSelectedProduct(item.consumable); setShowProductModal(true); }} className="p-0.5 hover:bg-slate-200 dark:hover:bg-slate-700 rounded flex-shrink-0"><Eye className="h-3.5 w-3.5 text-slate-400" /></button>
                                    </div>
                                    <p className="text-[11px] text-slate-400">Lot de {batchSize} {item.consumable.unit}</p>
                                  </div>
                                  {showTierBadge && (
                                    <span className="px-2 py-0.5 rounded-md bg-emerald-500 text-white text-[10px] font-bold flex-shrink-0">P{tierNumber}</span>
                                  )}
                                </div>
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-3 text-xs">
                                    <div>
                                      <span className="text-slate-400">Unit. </span>
                                      {hasDiscount && <span className="text-slate-400 line-through mr-1">{baseUnitPrice.toFixed(2)}€</span>}
                                      <span className={`font-medium ${hasDiscount ? 'text-emerald-600' : 'text-slate-600'}`}>{unitPrice.toFixed(2)}€</span>
                                    </div>
                                    <div>
                                      <span className="text-slate-400">Lot </span>
                                      {hasDiscount && <span className="text-slate-400 line-through mr-1">{baseLotPrice.toFixed(2)}€</span>}
                                      <span className={`font-semibold ${hasDiscount ? 'text-emerald-600' : 'text-slate-600'}`}>{lotPrice.toFixed(2)}€</span>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <div className="flex items-center">
                                      <button
                                        type="button"
                                        onClick={() => setProductQuantityDirect(item.consumable.id, quantity - minOrderQty < minOrderQty ? 0 : quantity - minOrderQty)}
                                        className={`w-7 h-8 flex items-center justify-center rounded-l-lg border border-r-0 text-sm font-bold transition-colors ${quantity > 0 ? 'border-cyan-300 bg-cyan-50 text-cyan-600 active:bg-cyan-100' : 'border-slate-200 bg-slate-100 text-slate-400'}`}
                                      >−</button>
                                      <input
                                        type="number"
                                        min={0}
                                        value={quantity === 0 ? '' : quantity}
                                        onChange={(e) => setProductQuantityDirect(item.consumable.id, e.target.value === '' ? 0 : (Number(e.target.value) || 0))}
                                        onBlur={(e) => { if (e.target.value === '') setProductQuantityDirect(item.consumable.id, 0); }}
                                        placeholder="0"
                                        className={`w-10 h-8 border-y px-0 text-center text-sm font-semibold focus:outline-none ${quantity > 0 ? 'border-cyan-300 bg-white text-cyan-600' : 'border-slate-200 bg-slate-50 text-slate-900'}`}
                                      />
                                      <button
                                        type="button"
                                        onClick={() => setProductQuantityDirect(item.consumable.id, quantity === 0 ? minOrderQty : quantity + minOrderQty)}
                                        className={`w-7 h-8 flex items-center justify-center rounded-r-lg border border-l-0 text-sm font-bold transition-colors ${quantity > 0 ? 'border-cyan-300 bg-cyan-50 text-cyan-600 active:bg-cyan-100' : 'border-slate-200 bg-slate-100 text-slate-400 active:bg-slate-200'}`}
                                      >+</button>
                                    </div>
                                    {quantity > 0 ? (<span className="text-sm font-bold text-slate-900 dark:text-slate-100 min-w-[50px] text-right">{lineTotal.toFixed(2)}€</span>) : <span className="text-sm text-slate-300 min-w-[50px] text-right">—</span>}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Remise supplémentaire section */}
            <div className="px-5 py-3 bg-amber-50/50 dark:bg-amber-900/10 border-y border-amber-100 dark:border-amber-800/30">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 rounded-lg bg-amber-100 dark:bg-amber-900/30"><Euro className="h-4 w-4 text-amber-600" /></div>
                <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">Remise supplémentaire</span>
              </div>
              {/* Type selector */}
              <div className="flex gap-2 mb-2">
                <button
                  type="button"
                  onClick={() => setCreditNoteType('amount')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${creditNoteType === 'amount' ? 'bg-amber-500 text-white' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600'}`}
                >
                  Montant (€)
                </button>
                <button
                  type="button"
                  onClick={() => setCreditNoteType('percentage')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${creditNoteType === 'percentage' ? 'bg-amber-500 text-white' : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600'}`}
                >
                  Pourcentage (%)
                </button>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex-1">
                  {creditNoteType === 'amount' ? (
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={creditNoteAmount || ''}
                      onChange={(e) => setCreditNoteAmount(parseFloat(e.target.value) || 0)}
                      placeholder="Montant HT (€)"
                      className="w-full h-9 px-3 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
                    />
                  ) : (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={creditNotePercentage || ''}
                        onChange={(e) => setCreditNotePercentage(parseFloat(e.target.value) || 0)}
                        placeholder="Réduction %"
                        className="w-full h-9 px-3 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
                      />
                      <span className="text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap">
                        ({(recapTotals.discountedTotal * (creditNotePercentage / 100)).toFixed(2)} €)
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex-[2]">
                  <input
                    type="text"
                    value={creditNoteReason}
                    onChange={(e) => setCreditNoteReason(e.target.value)}
                    placeholder="Justification (ex: erreur de facturation précédente)"
                    className="w-full h-9 px-3 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/50 focus:border-amber-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Récapitulatif - above commander button */}
          <div className="lg:hidden bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
            <div className="p-4 bg-slate-50/50 dark:bg-slate-800/30">
              <div className="flex items-center gap-2 mb-3">
                <div className="p-1.5 rounded-lg bg-cyan-100 dark:bg-cyan-900/30"><ShoppingCart className="h-4 w-4 text-cyan-600" /></div>
                <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">Récapitulatif</span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">Articles</span><span className="font-medium text-slate-900 dark:text-slate-100">{cart.length}</span></div>
                {recapTotals.baseTotal > recapTotals.discountedTotal && <div className="flex justify-between text-slate-400 line-through"><span>Prix de base HT</span><span>{recapTotals.baseTotal.toFixed(2)} €</span></div>}
                <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">Sous-total HT</span><span className="font-medium text-slate-900 dark:text-slate-100">{recapTotals.discountedTotal.toFixed(2)} €</span></div>
                {recapTotals.gainTotal > 0 && <div className="flex justify-between text-emerald-600"><span>Économie</span><span className="font-semibold">-{recapTotals.gainTotal.toFixed(2)} €</span></div>}
                {recapTotals.creditNoteValue > 0 && <div className="flex justify-between text-amber-600"><span>Remise supplémentaire</span><span className="font-semibold">-{recapTotals.creditNoteValue.toFixed(2)} €</span></div>}
                {recapTotals.creditNoteValue > 0 && <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">Net HT</span><span className="font-bold text-slate-900 dark:text-slate-100">{recapTotals.finalHt.toFixed(2)} €</span></div>}
                <div className="flex justify-between"><span className="text-slate-500 dark:text-slate-400">TVA {TVA_RATE}%</span><span className="font-medium text-slate-900 dark:text-slate-100">{recapTotals.taxAmount.toFixed(2)} €</span></div>
                <div className="pt-2 border-t border-slate-200 dark:border-slate-700 flex justify-between"><span className="font-bold text-slate-900 dark:text-slate-100">Total TTC</span><span className="font-bold text-cyan-600">{recapTotals.totalTtc.toFixed(2)} €</span></div>
              </div>
            </div>
          </div>

          <div className="sticky bottom-3 sm:bottom-6 bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100 dark:from-slate-800 dark:to-slate-900 rounded-2xl shadow-2xl p-3 sm:p-5 text-slate-900 dark:text-white border border-orange-200 dark:border-slate-700 ring-1 ring-orange-200/60 dark:ring-0">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-6">
              <div className="flex-1">
                <div className="flex items-baseline gap-2 sm:gap-3">
                  <span className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">{recapTotals.finalHt.toFixed(2)} €</span>
                  <span className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">HT</span>
                  {recapTotals.baseTotal > recapTotals.finalHt && <span className="text-xs text-slate-400 dark:text-slate-500 line-through ml-1">{recapTotals.baseTotal.toFixed(2)} €</span>}
                </div>
                {recapTotals.creditNoteValue > 0 && (
                  <div className="mt-1 inline-flex items-center gap-2 bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400 px-2 py-1 rounded-lg text-xs font-medium">
                    <Euro className="h-3.5 w-3.5" />
                    {creditNoteType === 'amount' ? `Remise supplémentaire : -${recapTotals.creditNoteValue.toFixed(2)} €` : `Remise supplémentaire : -${creditNotePercentage.toFixed(1)}% (${recapTotals.creditNoteValue.toFixed(2)} €)`}
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 sm:mt-3 text-xs sm:text-sm">
                  <span className="text-slate-600 dark:text-slate-400">TVA {TVA_RATE}% : {recapTotals.taxAmount.toFixed(2)} €</span>
                  <span className="text-slate-900 dark:text-white font-semibold">TTC : {recapTotals.totalTtc.toFixed(2)} €</span>
                  {recapTotals.gainTotal > 0 && (
                    <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-medium">
                      <Check className="h-3.5 w-3.5" />-{recapTotals.gainTotal.toFixed(2)} €
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-2 w-full sm:w-auto">
                {validationErrors.length > 0 && (
                  <div className="p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-xl">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
                      <ul className="text-xs text-red-700 dark:text-red-300 space-y-0.5">
                        {validationErrors.map((err, idx) => <li key={idx}>{err}</li>)}
                      </ul>
                    </div>
                  </div>
                )}
                <button onClick={() => setShowConfirmModal(true)} disabled={!canSubmit() || submitting} className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-orange-500 hover:bg-orange-400 disabled:bg-amber-200 disabled:text-amber-700 text-white rounded-xl font-semibold text-sm sm:text-base shadow-lg shadow-orange-500/30 hover:shadow-orange-500/40 transition-all disabled:shadow-none disabled:cursor-not-allowed">
                  {submitting ? 'Création...' : 'Créer la commande'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-lg w-full">
            <div className="p-6 border-b border-slate-200 dark:border-slate-700">
              <h2 className="text-xl font-bold">Confirmer la commande</h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4 space-y-1">
                <p className="text-sm text-slate-600"><strong>Articles :</strong> {totalItems}</p>
                <p className="text-sm text-slate-600"><strong>Sous-total HT :</strong> {recapTotals.discountedTotal.toFixed(2)} €</p>
                {recapTotals.creditNoteValue > 0 && (
                  <p className="text-sm text-amber-600"><strong>Remise supplémentaire :</strong> -{recapTotals.creditNoteValue.toFixed(2)} €</p>
                )}
                <p className="text-sm text-slate-600"><strong>Net HT :</strong> {recapTotals.finalHt.toFixed(2)} €</p>
                <p className="text-sm text-slate-600"><strong>TVA {TVA_RATE}% :</strong> {recapTotals.taxAmount.toFixed(2)} €</p>
                <p className="text-sm text-slate-600"><strong>Total TTC :</strong> {recapTotals.totalTtc.toFixed(2)} €</p>
                {recapTotals.gainTotal > 0 && (
                  <p className="text-sm text-emerald-600"><strong>Économie :</strong> {recapTotals.gainTotal.toFixed(2)} €</p>
                )}
              </div>
              <div className="space-y-2">
                {cart.map(item => {
                  const batchSize = item.consumable.batch_size ?? 1;
                  const pricing = item.pricingCalculation;
                  const lineTotal = pricing
                    ? pricing.total_cost
                    : item.quantity * item.unitPrice * batchSize;
                  return (
                    <div key={item.consumable.id} className="flex items-center justify-between text-sm text-slate-600">
                      <div className="min-w-0">
                        <p className="font-medium truncate">{item.consumable.title}</p>
                        <p className="text-xs text-slate-400">{item.quantity} lot{item.quantity > 1 ? 's' : ''} · {batchSize} {item.consumable.unit}</p>
                      </div>
                      <span className="font-semibold text-slate-700">{lineTotal.toFixed(2)} €</span>
                    </div>
                  );
                })}
              </div>
              {selectedSalesPointName && (
                <p className="text-sm text-slate-600"><strong>Point de vente :</strong> {selectedSalesPointName}</p>
              )}
              {selectedShippingAddress && (() => {
                const addr = filteredAddresses('shipping').find(a => a.id === selectedShippingAddress);
                return addr ? (
                  <p className="text-sm text-slate-600"><strong>Livraison :</strong> {addr.address}, {addr.postal_code} {addr.city}</p>
                ) : null;
              })()}
              {notes.trim() && (
                <p className="text-sm text-slate-600"><strong>Notes :</strong> {notes}</p>
              )}
            </div>
            <div className="p-6 border-t border-slate-200 dark:border-slate-700 flex gap-3">
              <button onClick={() => setShowConfirmModal(false)} className="flex-1 px-4 py-3 border border-slate-300 rounded-xl font-medium">Annuler</button>
              <button onClick={submit} disabled={submitting} className="flex-1 px-4 py-3 bg-cyan-500 hover:bg-cyan-400 text-white rounded-xl font-medium disabled:bg-slate-400">
                {submitting ? 'Création...' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showProductModal && selectedProduct && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowProductModal(false)}>
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white dark:bg-slate-900 px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <h2 className="text-xl font-bold truncate">{selectedProduct.title}</h2>
              <button onClick={() => setShowProductModal(false)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6 space-y-6">
              {(() => {
                let images = (selectedProduct as any).images;
                if (typeof images === 'string') { try { images = JSON.parse(images); } catch { images = []; } }
                if (!Array.isArray(images)) images = [];
                return images.length > 0 ? (
                  <div className="aspect-video rounded-xl overflow-hidden">
                    <Carousel images={images} alt={selectedProduct.title} className="w-full h-full" showDots={true} showArrows={true} autoPlay={false} />
                  </div>
                ) : null;
              })()}
              {selectedProduct.description && (
                <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-5">
                  <h3 className="text-sm font-semibold text-slate-500 uppercase mb-2">Description</h3>
                  <p className="text-slate-700 dark:text-slate-300">{selectedProduct.description}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white dark:bg-slate-800 rounded-xl p-5 border border-slate-200 dark:border-slate-700">
                  <h3 className="text-sm font-semibold text-slate-500 uppercase mb-3 flex items-center gap-2"><Package className="h-4 w-4" />Informations</h3>
                  <div className="space-y-2 text-sm">
                    {selectedProduct.marque && <div className="flex justify-between"><span className="text-slate-500">Marque</span><span className="font-medium">{selectedProduct.marque}</span></div>}
                    {selectedProduct.gamme && <div className="flex justify-between"><span className="text-slate-500">Gamme</span><span className="font-medium">{selectedProduct.gamme}</span></div>}
                    <div className="flex justify-between"><span className="text-slate-500">Unité</span><span className="font-medium">{selectedProduct.unit}</span></div>
                    {selectedProduct.batch_size && <div className="flex justify-between"><span className="text-slate-500">Lot</span><span className="font-medium">{selectedProduct.batch_size}</span></div>}
                  </div>
                </div>
                <div className="bg-cyan-50 dark:bg-cyan-950/30 rounded-xl p-5 border-2 border-cyan-200 dark:border-cyan-800">
                  <h3 className="text-sm font-semibold text-cyan-700 uppercase mb-3 flex items-center gap-2"><Tag className="h-4 w-4" />Prix</h3>
                  <p className="text-3xl font-bold text-cyan-600">{getPrice(selectedProduct.id).toFixed(2)} €</p>
                  <p className="text-sm text-slate-600 mt-1">/ {selectedProduct.unit}</p>
                  {selectedProduct.batch_size && selectedProduct.batch_size > 1 && <p className="text-sm text-slate-600 mt-2"><strong>{(getPrice(selectedProduct.id) * selectedProduct.batch_size).toFixed(2)} €</strong> par lot de {selectedProduct.batch_size}</p>}
                </div>
              </div>
              <div className="flex gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
                <button onClick={() => { setProductQuantityDirect(selectedProduct.id, (productQuantities.get(selectedProduct.id) || 0) + 1); setShowProductModal(false); }} className="flex-1 px-4 py-3 bg-cyan-500 hover:bg-cyan-400 text-white rounded-lg font-medium"><Plus className="h-5 w-5 inline mr-2" />Ajouter au panier</button>
                <button onClick={() => setShowProductModal(false)} className="px-4 py-3 border border-slate-300 rounded-lg font-medium">Fermer</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuyerNewOrderTailwind;
